/**
 * TenantContext — resolves the active tenant row from `v9_tenants` at boot.
 *
 * Reads `VITE_TENANT_SLUG` (env) with `tenant.config.json` fallback, queries
 * the master Supabase DB for the matching v9_tenants row, exposes it to the
 * rest of the app. Also fetches the active user's role from
 * `v9_tenant_memberships` once they sign in.
 *
 * Per-client forks just change `VITE_TENANT_SLUG` (set by the spin-up CLI) —
 * the schema, DB, and code are identical across every client deploy.
 */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getSupabase } from '../lib/supabase';
import { getTenantSlug, type ResolvedTenant } from '../lib/tenant';
import { useAuth } from './AuthContext';
import type { UserRole } from '../types';

interface TenantContextValue {
  tenant: ResolvedTenant | null;
  tenantError: string | null;
  membership: { tenantId: string; role: UserRole } | null;
  loading: boolean;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
  const supabase = getSupabase();
  const { session } = useAuth();
  const [tenant, setTenant] = useState<ResolvedTenant | null>(null);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const [membership, setMembership] = useState<{ tenantId: string; role: UserRole } | null>(null);
  const [loading, setLoading] = useState(true);

  // Resolve tenant from slug once (independent of auth state).
  useEffect(() => {
    let cancelled = false;
    const slug = getTenantSlug();
    (async () => {
      const { data, error } = await supabase
        .from('v9_tenants')
        .select('id, slug, name, primary_domain, admin_domain, isolation_mode, status, branding, features')
        .eq('slug', slug)
        .maybeSingle();

      if (cancelled) return;
      if (error) {
        setTenantError(`Failed to resolve tenant "${slug}": ${error.message}`);
        setLoading(false);
        return;
      }
      if (!data) {
        setTenantError(`No tenant row found for slug "${slug}". Insert one into v9_tenants and try again.`);
        setLoading(false);
        return;
      }
      setTenant(data as ResolvedTenant);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [supabase]);

  // Re-fetch membership whenever the user logs in / out.
  useEffect(() => {
    let cancelled = false;
    if (!tenant || !session?.user) {
      setMembership(null);
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from('v9_tenant_memberships')
        .select('tenant_id, role')
        .eq('user_id', session.user.id)
        .eq('tenant_id', tenant.id)
        .maybeSingle();

      if (cancelled) return;
      if (error || !data) {
        setMembership(null);
        return;
      }
      setMembership({ tenantId: data.tenant_id, role: data.role as UserRole });
    })();
    return () => { cancelled = true; };
  }, [supabase, tenant, session?.user?.id]);

  return (
    <TenantContext.Provider value={{ tenant, tenantError, membership, loading }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within TenantProvider');
  return ctx;
}
